import React from 'react';
import { Check, Sparkles, Award, Heart } from 'lucide-react';

const Products = () => {
  const products = [
    {
      id: 1,
      name: 'Amyrah Everyday',
      tagline: 'Premium Quality for Daily Delight',
      image: 'https://customer-assets.emergentagent.com/job_pack-preview-1/artifacts/sj8o8lv1_EVERYDAY1KG.MAIN.png',
      color: 'from-red-500 to-rose-600',
      bgColor: 'from-red-50 to-rose-50',
      description: 'Perfect for daily cooking, Amyrah Everyday brings premium Basmati rice to your table every day. With excellent aroma, taste, and texture, it makes every meal special.',
      features: [
        'Premium long grain Basmati',
        'Perfect for daily meals',
        'Aromatic and flavorful',
        'Excellent value for money',
        'Consistent quality',
        'Ideal for all rice dishes'
      ],
      specifications: {
        'Aging': 'Aged for optimal taste',
        'Purity': '100% Pure Basmati',
        'Available in': '1kg, 5kg, 30kg'
      }
    },
    {
      id: 2,
      name: 'Amyrah Excel',
      tagline: 'Superior Excellence for Special Moments',
      image: 'https://customer-assets.emergentagent.com/job_pack-preview-1/artifacts/4fzai17a_EXCEL1KG.MAIN.png',
      color: 'from-blue-600 to-indigo-600',
      bgColor: 'from-blue-50 to-indigo-50',
      description: 'Amyrah Excel offers superior quality and exceptional taste, making it perfect for festive occasions and special gatherings. Experience excellence in every bite.',
      features: [
        'Extra long grain Basmati',
        'Superior aroma and taste',
        'Perfect for celebrations',
        'Elegant grain elongation',
        'Premium quality assured',
        'Ideal for biryani and pulao'
      ],
      specifications: {
        'Aging': 'Specially aged',
        'Purity': '100% Pure Basmati',
        'Available in': '1kg, 5kg, 30kg'
      }
    },
    {
      id: 3,
      name: 'Amyrah Supreme',
      tagline: 'The Ultimate Luxury Experience',
      image: 'https://customer-assets.emergentagent.com/job_pack-preview-1/artifacts/qaiy3b7m_SUPREME1KG.MAIN.png',
      color: 'from-cyan-500 to-teal-600',
      bgColor: 'from-cyan-50 to-teal-50',
      description: 'The pinnacle of Basmati excellence, Amyrah Supreme features premium quality and most exquisite aroma. Reserved for the most discerning palates and grandest occasions.',
      features: [
        'Extra-long premium grains',
        'Unmatched aroma',
        'Ultimate luxury experience',
        'Maximum grain elongation',
        'Supreme quality',
        'Perfect for royal feasts'
      ],
      specifications: {
        'Aging': 'Matured to perfection',
        'Purity': '100% Pure Basmati',
        'Available in': '1kg, 5kg, 30kg'
      }
    }
  ];

  return (
    <div className="min-h-screen" data-testid="products-page">
      {/* Hero Section */}
      <section 
        className="relative h-[60vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://images.pexels.com/photos/18328392/pexels-photo-18328392.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">Products</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            Premium Amyrah Basmati Rice Collection
          </p>
        </div>
      </section>

      {/* Brand Mascot Section */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <img 
                src="https://customer-assets.emergentagent.com/job_pack-preview-1/artifacts/1387u405_amyrah%20brand%20mascot.png" 
                alt="Meet Amyrah - Brand Mascot" 
                className="w-80 mx-auto lg:mx-0 drop-shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Meet Amyrah
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">
                  Our Brand Mascot
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Amyrah represents the warmth, care, and quality that goes into every pack of our premium Basmati rice. She embodies our commitment to bringing the finest grains to families around the world.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Just like Amyrah's friendly smile, our rice brings joy to every meal, making ordinary days special and celebrations unforgettable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      {products.map((product, index) => (
        <section 
          key={product.id} 
          className={`py-20 ${index % 2 === 0 ? 'bg-white' : `bg-gradient-to-br ${product.bgColor}`}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="inline-flex items-center space-x-2 mb-4">
                  <Sparkles className={`bg-gradient-to-r ${product.color} bg-clip-text text-transparent`} size={24} />
                  <span className={`text-sm font-semibold bg-gradient-to-r ${product.color} bg-clip-text text-transparent uppercase tracking-wide`}>
                    Premium Collection
                  </span>
                </div>
                <h2 className={`text-5xl font-bold mb-4 bg-gradient-to-r ${product.color} bg-clip-text text-transparent`}>
                  {product.name}
                </h2>
                <p className="text-xl text-gray-700 mb-6 italic">{product.tagline}</p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {product.description}
                </p>

                {/* Features */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Award className="mr-2 text-amber-600" size={24} />
                    Key Features
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Check className={`bg-gradient-to-r ${product.color} bg-clip-text text-transparent flex-shrink-0`} size={20} />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Specifications */}
                <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Specifications</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key}>
                        <p className="text-sm text-gray-500 mb-1">{key}</p>
                        <p className="font-semibold text-gray-900">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-12 shadow-2xl">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-auto max-h-[600px] object-contain drop-shadow-2xl transform hover:scale-105 transition-transform duration-500"
                  />
                </div>
                {/* Decorative Element */}
                <div className={`absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-r ${product.color} rounded-full blur-3xl opacity-30`}></div>
                <div className={`absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-r ${product.color} rounded-full blur-3xl opacity-30`}></div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Why Choose Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">Amyrah?</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Every grain tells a story of quality, tradition, and excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <WhyChooseCard 
              icon={Award}
              title="Premium Quality"
              description="100% pure Basmati rice with exceptional quality and aroma"
            />
            <WhyChooseCard 
              icon={Sparkles}
              title="Aged to Perfection"
              description="Specially aged rice for enhanced flavor and texture"
            />
            <WhyChooseCard 
              icon={Heart}
              title="Family Tradition"
              description="3rd generation expertise in selecting the finest grains"
            />
            <WhyChooseCard 
              icon={Check}
              title="Certified Excellence"
              description="International quality certifications and standards"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-600 via-yellow-500 to-amber-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Experience Premium Basmati?
          </h2>
          <p className="text-xl mb-8">
            Contact us for bulk orders, private labeling, or distribution inquiries
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="px-8 py-4 bg-white text-amber-600 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-xl"
              data-testid="request-inquiry-btn"
            >
              Request Inquiry
            </a>
            <a 
              href="https://wa.me/919810137856" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-8 py-4 bg-green-600 text-white rounded-full font-semibold text-lg hover:bg-green-700 transition-all duration-300 shadow-xl"
              data-testid="order-whatsapp-btn"
            >
              Order via WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const WhyChooseCard = ({ icon: Icon, title, description }) => {
  return (
    <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all duration-300">
      <div className="inline-flex p-4 bg-gradient-to-r from-amber-600 to-yellow-500 rounded-full mb-4">
        <Icon size={28} className="text-white" />
      </div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-gray-300 text-sm">{description}</p>
    </div>
  );
};

export default Products;